import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatoFechasEs } from "../../../../../functions/funciones";
import DataTable from "../../../../../components/Tables/DataTable";
import ViewModeToggle from "../../../../../components/Tables/ViewModeToggle";
import FiltrosProveedoresLegalia from "../../../../../components/filtros/FiltrosProveedoresLegalia";
import Empty from "../../../../../icons/empty.png";
import { IdCard, Mailbox, MapPin, Wrench, Phone } from "lucide-react";

const ScoreCircle = ({ score, size = 44 }) => {
  const target = Math.min(Math.max(score || 0, 0), 100);
  const color =
    target === 0
      ? "#ef4444"
      : target <= 50
      ? "#eab308"
      : "#22c55e";

  const center = size / 2;
  const strokeWidth = size >= 80 ? 4 : 3;
  const r = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * r;
  const targetDash = (target / 100) * circumference;
  const fontSize = Math.max(10, Math.round(size * 0.24));

  const [dash, setDash] = useState(0);
  const [displayScore, setDisplayScore] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const start = performance.now();
    const duration = 800;
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = ease(p);
      setDash(targetDash * eased);
      setDisplayScore(Math.round(target * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, targetDash]);

  return (
    <span className="inline-flex items-center justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={`${dash} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fontSize}
          fontWeight="700"
          fill={color}
        >
          {displayScore}%
        </text>
      </svg>
    </span>
  );
};

const ServicioChips = ({ servicios, tc, justifyEnd = false }) => (
  <div className={`flex flex-wrap gap-1 ${justifyEnd ? "justify-end" : ""}`}>
    {(servicios || []).map((s) => (
      <span
        key={s.id}
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
      >
        {tc(`serviceNames.${s.nombre}`, s.nombre)}
      </span>
    ))}
  </div>
);

function ProveedoresLegalia({
  t,
  user,
  data,
  loading,
  fetchData,
  busqueda,
  filtrarDatos,
  busquedaActiva,
  setBusquedaActiva,
  clearFilterActivo,
  setClearFilterActivo,
}) {
  const { t: tc } = useTranslation('common');
  const meta = data.meta || {};
  const rows = data.rows || [];

  const [viewMode, setViewMode] = useState("cards");

  const proveedoresRows = [
    {
      id: "razonSocial",
      label: t('columns.supplier'),
      overrideFunc: (data, row) => (
        <>
          <Link to={`/proveedores/${row.nif}`} className="font-medium text-gray-900 hover:underline">
            {row.razonSocial}
          </Link>
          <div className="text-xs text-gray-400 mt-0.5">
            {row.nif}
          </div>
        </>
      ),
    },
    {
      id: "telefonoContacto",
      label: t('columns.phone'),
    },
    {
      id: "provincia",
      label: t('columns.province'),
      overrideFunc: (data, row) => (
        <>
          <span className="font-medium text-[15px]">{row.provincia}</span>
          <div className="text-xs text-gray-400 mt-0.5">{row.municipio}</div>
        </>
      ),
    },
    {
      id: "codigoPostal",
      label: t('columns.postalCode'),
      overrideFunc: (data) => <span className="ml-6">{data || ""}</span>,
    },
    {
      id: "cumplimiento",
      label: t('columns.compliance'),
      overrideFunc: (data) => (
        <span className="inline-flex ml-6">
          <ScoreCircle score={Math.round(data || 0)} />
        </span>
      ),
    },
    {
      id: "ultimaReclamacion",
      label: t('columns.lastClaim'),
      overrideFunc: (data) => <span className="ml-6">{data ? formatoFechasEs(data) : "-"}</span>,
    },
    {
      id: "servicios",
      label: t('columns.services'),
      noObjeto: true,
      overrideFunc: (data) => (
        <div className="max-w-[280px]">
          <ServicioChips servicios={data} tc={tc} />
        </div>
      ),
    },
  ];

  const renderProveedorCard = (row, index = 0) => {
    const provLoc = [row.provincia, row.municipio].filter(Boolean).join(", ");

    return (
      <div
        style={{
          background: "linear-gradient(135deg, rgba(255, 255, 255, 0.94) 0%, rgba(229, 231, 235, 0.55) 100%)",
          borderRadius: 14,
          padding: "18px 20px",
          border: "1px solid rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 3px 12px rgba(17, 24, 39, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
          height: "100%",
          animationDelay: `${Math.min(index, 12) * 40}ms`,
        }}
        className="flex flex-col legalia-card-enter"
      >
        {/* Header: nombre + NIF + datos */}
        <div>
          <Link
            to={`/proveedores/${row.nif}`}
            className="font-semibold text-gray-900 hover:underline text-base flex items-start gap-2 break-words"
          >
            <Wrench className="text-[#3651d8] w-5 h-5 shrink-0 mt-0.5" strokeWidth={1.5} />
            <span className="min-w-0">{row.razonSocial}</span>
          </Link>
          <div className="text-xs text-gray-900 mt-1 truncate">
            <IdCard className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />
            {row.nif || "-"}
          </div>
          <div className="text-xs text-gray-900 mt-1 truncate">
            <Phone className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />
            {row.telefonoContacto || "-"}
          </div>
          <div className="text-xs text-gray-900 mt-1 truncate">
            <MapPin className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />
            {row.municipio || "-"}
          </div>
          <div className="text-xs text-gray-900 mt-1 truncate">
            <Mailbox className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />
            {row.codigoPostal || "-"}
          </div>
        </div>

        {/* Bloque inferior: porcentaje + 2 columnas (anclado abajo, alineado entre cards) */}
        <div className="mt-auto">
          {/* Porcentaje grande centrado */}
          <div className="flex flex-col items-center justify-center py-2 border-y border-gray-100 my-3">
            <ScoreCircle score={Math.round(row.cumplimiento || 0)} size={110} />
            <div className="text-[11px] text-gray-900 mt-1 uppercase tracking-wide">
              {t('columns.compliance')}
            </div>
          </div>

          {/* 2 columnas: Última reclamación | Servicios */}
          <div className="flex justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-gray-900 uppercase tracking-wide mb-1">
                {t('columns.lastClaim')}
              </div>
              <div className="text-sm text-gray-700">
                {row.ultimaReclamacion ? formatoFechasEs(row.ultimaReclamacion) : "-"}
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-gray-900 uppercase tracking-wide mb-1 text-right">
                {t('columns.services')}
              </div>
              {row.servicios?.length > 0 ? (
                <div className="flex justify-end gap-1 min-w-0">
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 whitespace-nowrap min-w-0 truncate"
                  >
                    {tc(`serviceNames.${row.servicios[0].nombre}`, row.servicios[0].nombre)}
                  </span>
                  {row.servicios.length > 1 && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600 whitespace-nowrap shrink-0 cursor-help"
                      title={row.servicios.slice(1).map(s => tc(`serviceNames.${s.nombre}`, s.nombre)).join(", ")}
                    >
                      +{row.servicios.length - 1}
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-400 text-right">-</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-8xl mx-auto bg-white shadow-sm p-3 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-5">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('title')}</h1>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>

        <FiltrosProveedoresLegalia
          onFilter={filtrarDatos}
          filtrosIniciales={busqueda}
          user={user}
        />

        <DataTable
          variant="legalia"
          viewMode={viewMode}
          renderCard={renderProveedorCard}
          data={rows}
          meta={meta}
          headers={proveedoresRows}
          isLoading={loading}
          fetcher={fetchData}
          filtros={busqueda}
          busquedaActiva={busquedaActiva}
          setBusquedaActiva={setBusquedaActiva}
          clearFilterActivo={clearFilterActivo}
          setClearFilterActivo={setClearFilterActivo}
          mensaje={
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <img src={Empty} alt="Empty" style={{ width: 75 }} />
              <p className="text-base font-semibold text-gray-900">{t('noSuppliers')}</p>
              <p className="text-sm text-gray-900 text-center max-w-xs">{t('noSuppliersAvailable')}</p>
            </div>
          }
        />
      </div>
    </div>
  );
}

export default ProveedoresLegalia;
